import { useState, useEffect, useRef, useMemo } from 'react';
import api from '@/lib/api';
import { Download, Upload, RefreshCw, Clock, AlertTriangle, CheckCircle2, Database, Shield, Trash2, Pin, Search, Info } from 'lucide-react';

const fmtBytes = (n) => {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });
};

export default function SnapshotManager() {
  const [preview, setPreview] = useState(null);
  const [autoList, setAutoList] = useState([]);
  const [goldList, setGoldList] = useState([]);
  const [loading, setLoading] = useState(true);    // initial only
  const [refreshing, setRefreshing] = useState(false); // manual refresh
  const [busy, setBusy] = useState('');
  const [flash, setFlash] = useState(null);
  const [showRestore, setShowRestore] = useState(false);
  const [restoreConfirm, setRestoreConfirm] = useState('');
  const [restoreFile, setRestoreFile] = useState(null);
  const [restoreResult, setRestoreResult] = useState(null);
  const [showPin, setShowPin] = useState(false);
  const [pinLabel, setPinLabel] = useState('');
  const [pinNote, setPinNote] = useState('');
  // Search + sort for the snapshots list.
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('newest'); // newest | oldest | largest | smallest
  const fileRef = useRef(null);

  const loadAll = async ({ manual = false } = {}) => {
    if (manual) setRefreshing(true);
    try {
      const [p, l] = await Promise.all([
        api.get('/admin/snapshot/preview'),
        api.get('/admin/snapshot/list'),
      ]);
      setPreview(p.data.meta);
      setAutoList(l.data.snapshots || []);
      setGoldList(l.data.gold_masters || []);
      if (manual) showFlash('ok', `Refreshed · ${(l.data.snapshots || []).length} auto · ${(l.data.gold_masters || []).length} gold`);
    } catch (e) {
      setFlash({ type: 'error', msg: e?.response?.data?.detail || 'Failed to load snapshot info' });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { loadAll(); }, []);

  const showFlash = (type, msg) => {
    setFlash({ type, msg });
    setTimeout(() => setFlash(null), 5000);
  };

  const downloadMain = async () => {
    setBusy('download');
    try {
      const baseURL = api.defaults.baseURL;
      // Use fetch for binary so Authorization cookie is sent
      const res = await fetch(`${baseURL}/admin/snapshot/download`, { credentials: 'include' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const ts = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `scaleboard_snapshot_${ts}Z.json`;
      a.click();
      URL.revokeObjectURL(a.href);
      showFlash('ok', `Downloaded snapshot · ${fmtBytes(blob.size)}`);
    } catch (e) {
      showFlash('error', e.message || 'Download failed');
    } finally {
      setBusy('');
    }
  };

  const downloadAuto = async (name) => {
    setBusy(name);
    try {
      const baseURL = api.defaults.baseURL;
      const res = await fetch(`${baseURL}/admin/snapshot/download-auto/${encodeURIComponent(name)}`, { credentials: 'include' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = name;
      a.click();
      URL.revokeObjectURL(a.href);
      showFlash('ok', `Downloaded ${name}`);
    } catch (e) {
      showFlash('error', e.message || 'Download failed');
    } finally {
      setBusy('');
    }
  };

  const runNow = async () => {
    setBusy('run');
    try {
      const r = await api.post('/admin/snapshot/run-now');
      showFlash('ok', `Snapshot written: ${r.data.file} (${r.data.total_documents} docs)`);
      await loadAll();
    } catch (e) {
      showFlash('error', e?.response?.data?.detail || 'Run failed');
    } finally {
      setBusy('');
    }
  };

  const submitPin = async () => {
    const label = pinLabel.trim();
    if (!label) { showFlash('error', 'Label is required (e.g., "Pre-UAT Gold Master")'); return; }
    setBusy('pin');
    try {
      const r = await api.post('/admin/snapshot/pin', { label, note: pinNote.trim() });
      showFlash('ok', `Pinned "${r.data.label}" · ${r.data.total_documents} docs · ${r.data.size_mb} MB`);
      setShowPin(false);
      setPinLabel('');
      setPinNote('');
      await loadAll();
    } catch (e) {
      showFlash('error', e?.response?.data?.detail || 'Pin failed');
    } finally {
      setBusy('');
    }
  };

  const deleteGold = async (name, label) => {
    if (!window.confirm(`Delete Gold Master "${label || name}"? This cannot be undone.`)) return;
    setBusy(name);
    try {
      await api.delete(`/admin/snapshot/gold-master/${encodeURIComponent(name)}`);
      showFlash('ok', `Deleted ${name}`);
      await loadAll();
    } catch (e) {
      showFlash('error', e?.response?.data?.detail || 'Delete failed');
    } finally {
      setBusy('');
    }
  };

  const submitRestore = async () => {    if (!restoreFile) { showFlash('error', 'Pick a JSON file first'); return; }
    if (restoreConfirm !== 'RESTORE-SCALEBOARD') {
      showFlash('error', "Confirm phrase must be exactly 'RESTORE-SCALEBOARD'");
      return;
    }
    setBusy('restore');
    setRestoreResult(null);
    try {
      const fd = new FormData();
      fd.append('file', restoreFile);
      fd.append('confirm', restoreConfirm);
      const r = await api.post('/admin/snapshot/restore', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setRestoreResult(r.data);
      showFlash('ok', `Restored ${r.data.restored_total_documents} docs across ${r.data.restored_collections} collections`);
      setShowRestore(false);
      setRestoreConfirm('');
      setRestoreFile(null);
      if (fileRef.current) fileRef.current.value = '';
      await loadAll();
    } catch (e) {
      showFlash('error', e?.response?.data?.detail || 'Restore failed');
    } finally {
      setBusy('');
    }
  };

  // Filter + sort helpers (must run before any early return to satisfy
  // react-hooks/rules-of-hooks). Addresses "not moving and filtering properly".
  const filteredAuto = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = q
      ? autoList.filter((f) =>
          (f.name || '').toLowerCase().includes(q)
          || (f.label || '').toLowerCase().includes(q)
          || (f.note || '').toLowerCase().includes(q))
      : autoList;
    const sorters = {
      newest: (a, b) => (b.mtime || '').localeCompare(a.mtime || ''),
      oldest: (a, b) => (a.mtime || '').localeCompare(b.mtime || ''),
      largest: (a, b) => (b.size_bytes || 0) - (a.size_bytes || 0),
      smallest: (a, b) => (a.size_bytes || 0) - (b.size_bytes || 0),
    };
    return [...filtered].sort(sorters[sortBy] || sorters.newest);
  }, [autoList, search, sortBy]);
  const filteredGold = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = q
      ? goldList.filter((f) =>
          (f.name || '').toLowerCase().includes(q)
          || (f.label || '').toLowerCase().includes(q)
          || (f.note || '').toLowerCase().includes(q))
      : goldList;
    const sorters = {
      newest: (a, b) => (b.mtime || '').localeCompare(a.mtime || ''),
      oldest: (a, b) => (a.mtime || '').localeCompare(b.mtime || ''),
      largest: (a, b) => (b.size_bytes || 0) - (a.size_bytes || 0),
      smallest: (a, b) => (a.size_bytes || 0) - (b.size_bytes || 0),
    };
    return [...filtered].sort(sorters[sortBy] || sorters.newest);
  }, [goldList, search, sortBy]);

  if (loading) {
    return <div className="flex items-center justify-center py-20"><div className="w-6 h-6 border-2 border-[#2A7B88] border-t-transparent rounded-full animate-spin" /></div>;
  }

  const topCollections = preview
    ? Object.entries(preview.collection_counts || {}).sort((a, b) => b[1] - a[1]).slice(0, 8)
    : [];

  return (
    <div className="space-y-5" data-testid="snapshot-manager">
      {/* Flash */}
      {flash && (
        <div className="rounded-md px-3 py-2 text-xs font-medium flex items-center gap-2"
          style={flash.type === 'error'
            ? { backgroundColor: '#FEF2F2', color: '#D4726A' }
            : { backgroundColor: '#ECFDF5', color: '#2D8B5E' }}
          data-testid="snapshot-flash">
          {flash.type === 'error' ? <AlertTriangle size={14} /> : <CheckCircle2 size={14} />}
          {flash.msg}
        </div>
      )}

      {/* Header stats */}
      <div className="bg-white rounded-xl border p-5" style={{ borderColor: '#E5E1DC' }}>
        <div className="flex items-start justify-between gap-4 mb-4">
          <div className="flex items-center gap-2">
            <Database size={18} style={{ color: '#2A7B88' }} />
            <div>
              <h2 className="text-sm font-bold" style={{ color: '#1B2A4A' }}>Full Database Snapshot</h2>
              <p className="text-[11px]" style={{ color: '#6B7280' }}>
                Captures every collection — users, clients, contracts, daily sales, TDB products, SOPs, reviews, history, targets.
                Restore any previous snapshot with one click if UAT testing causes unexpected data drift.
              </p>
            </div>
          </div>
          <button onClick={() => loadAll({ manual: true })} disabled={refreshing}
            className="px-2.5 py-1.5 rounded-md border text-[11px] flex items-center gap-1 disabled:opacity-60"
            style={{ borderColor: '#E5E1DC', color: '#6B7280' }}
            data-testid="snapshot-refresh">
            <RefreshCw size={11} className={refreshing ? 'animate-spin' : ''} /> {refreshing ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-4">
          <div className="rounded-lg border p-3" style={{ borderColor: '#E5E1DC', backgroundColor: '#F9FAFB' }}>
            <p className="text-[10px] uppercase tracking-wider" style={{ color: '#9CA3AF' }}>Total Documents</p>
            <p className="text-lg font-bold" style={{ color: '#1B2A4A' }}>{(preview?.total_documents || 0).toLocaleString('en-IN')}</p>
          </div>
          <div className="rounded-lg border p-3" style={{ borderColor: '#E5E1DC', backgroundColor: '#F9FAFB' }}>
            <p className="text-[10px] uppercase tracking-wider" style={{ color: '#9CA3AF' }}>Collections</p>
            <p className="text-lg font-bold" style={{ color: '#1B2A4A' }}>{preview?.collection_total || 0}</p>
          </div>
          <div className="rounded-lg border p-3" style={{ borderColor: '#E5E1DC', backgroundColor: '#F9FAFB' }}>
            <p className="text-[10px] uppercase tracking-wider" style={{ color: '#9CA3AF' }}>Code Files</p>
            <p className="text-lg font-bold" style={{ color: '#1B2A4A' }} data-testid="preview-code-files">
              {preview?.code_stats?.files?.toLocaleString('en-IN') || 0}
              <span className="text-[10px] font-normal ml-1" style={{ color: '#9CA3AF' }}>
                · {preview?.code_stats ? ((preview.code_stats.bytes || 0) / 1024 / 1024).toFixed(2) : 0} MB
              </span>
            </p>
          </div>
          <div className="rounded-lg border p-3" style={{ borderColor: '#E5E1DC', backgroundColor: '#F9FAFB' }}>
            <p className="text-[10px] uppercase tracking-wider" style={{ color: '#9CA3AF' }}>Docs</p>
            <p className="text-lg font-bold" style={{ color: '#1B2A4A' }} data-testid="preview-doc-files">
              {preview?.docs_stats?.files?.toLocaleString('en-IN') || 0}
              <span className="text-[10px] font-normal ml-1" style={{ color: '#9CA3AF' }}>
                · {preview?.docs_stats ? ((preview.docs_stats.bytes || 0) / 1024).toFixed(1) : 0} KB
              </span>
            </p>
          </div>
          <div className="rounded-lg border p-3" style={{ borderColor: '#E5E1DC', backgroundColor: '#F9FAFB' }}>
            <p className="text-[10px] uppercase tracking-wider" style={{ color: '#9CA3AF' }}>DB Name</p>
            <p className="text-sm font-bold" style={{ color: '#1B2A4A' }}>{preview?.db_name || '—'}</p>
          </div>
          <div className="rounded-lg border p-3" style={{ borderColor: '#E5E1DC', backgroundColor: '#F9FAFB' }}>
            <p className="text-[10px] uppercase tracking-wider" style={{ color: '#9CA3AF' }}>Format Version</p>
            <p className="text-sm font-bold" style={{ color: '#1B2A4A' }}>v{preview?.version || '—'}</p>
          </div>
        </div>

        {/* Action row */}
        <div className="flex flex-wrap items-center gap-2 pt-3 border-t" style={{ borderColor: '#E5E1DC' }}>
          <button onClick={downloadMain} disabled={busy === 'download'}
            className="px-4 py-2 rounded-md text-xs font-semibold text-white flex items-center gap-1.5 disabled:opacity-60"
            style={{ backgroundColor: '#2A7B88' }}
            data-testid="snapshot-download-btn">
            <Download size={13} /> {busy === 'download' ? 'Preparing…' : 'Download Full Snapshot'}
          </button>
          <button onClick={runNow} disabled={busy === 'run'}
            className="px-3 py-2 rounded-md border text-xs font-medium flex items-center gap-1.5 disabled:opacity-60"
            style={{ borderColor: '#E5E1DC', color: '#1B2A4A' }}
            data-testid="snapshot-run-now-btn">
            <Clock size={12} /> {busy === 'run' ? 'Running…' : 'Run Auto-Backup Now'}
          </button>
          <button onClick={() => setShowPin(s => !s)}
            className="px-3 py-2 rounded-md border text-xs font-medium flex items-center gap-1.5"
            style={{ borderColor: '#1B2A4A', color: '#1B2A4A', backgroundColor: '#F9FAFB' }}
            data-testid="snapshot-pin-btn">
            <Shield size={12} /> Pin as Gold Master
          </button>
          <button onClick={() => setShowRestore(s => !s)}
            className="px-3 py-2 rounded-md border text-xs font-medium flex items-center gap-1.5"
            style={{ borderColor: '#FECACA', color: '#D4726A' }}
            data-testid="snapshot-restore-btn">
            <Upload size={12} /> Restore from File
          </button>
        </div>

        {/* Top collections preview */}
        {topCollections.length > 0 && (
          <div className="mt-4 pt-3 border-t" style={{ borderColor: '#E5E1DC' }}>
            <p className="text-[10px] uppercase tracking-wider mb-2" style={{ color: '#9CA3AF' }}>Largest collections</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-1">
              {topCollections.map(([name, count]) => (
                <div key={name} className="flex items-center justify-between text-[11px]">
                  <span style={{ color: '#6B7280' }} className="truncate">{name}</span>
                  <span className="font-semibold" style={{ color: '#1B2A4A' }}>{count.toLocaleString('en-IN')}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Restore panel */}
      {showRestore && (
        <div className="bg-white rounded-xl border p-5" style={{ borderColor: '#FECACA' }} data-testid="snapshot-restore-panel">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle size={16} style={{ color: '#D4726A' }} />
            <h3 className="text-sm font-bold" style={{ color: '#D4726A' }}>DESTRUCTIVE: Restore from JSON</h3>
          </div>
          <p className="text-[11px] mb-3" style={{ color: '#6B7280' }}>
            This will <b>wipe every collection</b> and replace with the contents of the uploaded snapshot. Intended for recovery after UAT anomalies.
            Type the phrase <code className="px-1 py-0.5 rounded" style={{ backgroundColor: '#FEF2F2', color: '#D4726A' }}>RESTORE-SCALEBOARD</code> to enable the button.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-wider" style={{ color: '#9CA3AF' }}>Snapshot file (.json)</label>
              <input ref={fileRef} type="file" accept="application/json,.json"
                onChange={e => setRestoreFile(e.target.files?.[0] || null)}
                className="mt-1 w-full text-xs border rounded px-2 py-1.5"
                style={{ borderColor: '#E5E1DC' }}
                data-testid="snapshot-restore-file" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider" style={{ color: '#9CA3AF' }}>Confirm phrase</label>
              <input type="text" value={restoreConfirm}
                onChange={e => setRestoreConfirm(e.target.value)}
                placeholder="RESTORE-SCALEBOARD"
                className="mt-1 w-full text-xs border rounded px-2 py-1.5 font-mono"
                style={{ borderColor: '#E5E1DC' }}
                data-testid="snapshot-restore-confirm" />
            </div>
          </div>
          <div className="flex items-center gap-2 mt-3">
            <button onClick={submitRestore}
              disabled={busy === 'restore' || restoreConfirm !== 'RESTORE-SCALEBOARD' || !restoreFile}
              className="px-4 py-2 rounded-md text-xs font-semibold text-white flex items-center gap-1.5 disabled:opacity-40"
              style={{ backgroundColor: '#D4726A' }}
              data-testid="snapshot-restore-submit">
              <Upload size={13} /> {busy === 'restore' ? 'Restoring…' : 'Restore Now'}
            </button>
            <button onClick={() => { setShowRestore(false); setRestoreConfirm(''); setRestoreFile(null); }}
              className="px-3 py-2 rounded-md border text-xs font-medium"
              style={{ borderColor: '#E5E1DC', color: '#6B7280' }}
              data-testid="snapshot-restore-cancel">
              Cancel
            </button>
          </div>
          {restoreResult && (
            <div className="mt-3 rounded-md p-2 text-[11px]" style={{ backgroundColor: '#ECFDF5', color: '#2D8B5E' }}>
              Restored {restoreResult.restored_total_documents} documents across {restoreResult.restored_collections} collections.
            </div>
          )}
        </div>
      )}

      {/* Pin as Gold Master panel */}
      {showPin && (
        <div className="bg-white rounded-xl border p-5" style={{ borderColor: '#1B2A4A' }} data-testid="snapshot-pin-panel">
          <div className="flex items-center gap-2 mb-2">
            <Shield size={16} style={{ color: '#1B2A4A' }} />
            <h3 className="text-sm font-bold" style={{ color: '#1B2A4A' }}>Pin current state as Gold Master</h3>
          </div>
          <p className="text-[11px] mb-3" style={{ color: '#6B7280' }}>
            A Gold Master is an <b>immutable rollback point</b> — it is never rotated out by the 7-day window.
            Use this to freeze a known-good state (e.g., "Pre-UAT gold master") that stays safe even weeks later.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-wider" style={{ color: '#9CA3AF' }}>Label*</label>
              <input type="text" value={pinLabel} onChange={e => setPinLabel(e.target.value)}
                placeholder="e.g., Pre-UAT gold master"
                className="mt-1 w-full text-xs border rounded px-2 py-1.5"
                style={{ borderColor: '#E5E1DC' }}
                data-testid="snapshot-pin-label" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider" style={{ color: '#9CA3AF' }}>Note (optional)</label>
              <input type="text" value={pinNote} onChange={e => setPinNote(e.target.value)}
                placeholder="e.g., Captured 19-Feb 08:05 IST before UAT emails go out"
                className="mt-1 w-full text-xs border rounded px-2 py-1.5"
                style={{ borderColor: '#E5E1DC' }}
                data-testid="snapshot-pin-note" />
            </div>
          </div>
          <div className="flex items-center gap-2 mt-3">
            <button onClick={submitPin} disabled={busy === 'pin' || !pinLabel.trim()}
              className="px-4 py-2 rounded-md text-xs font-semibold text-white flex items-center gap-1.5 disabled:opacity-40"
              style={{ backgroundColor: '#1B2A4A' }}
              data-testid="snapshot-pin-submit">
              <Pin size={13} /> {busy === 'pin' ? 'Pinning…' : 'Pin Now'}
            </button>
            <button onClick={() => { setShowPin(false); setPinLabel(''); setPinNote(''); }}
              className="px-3 py-2 rounded-md border text-xs font-medium"
              style={{ borderColor: '#E5E1DC', color: '#6B7280' }}
              data-testid="snapshot-pin-cancel">
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Gold Masters list */}
      {goldList.length > 0 && (
        <div className="bg-white rounded-xl border p-5" style={{ borderColor: '#1B2A4A' }} data-testid="snapshot-gold-list">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Shield size={16} style={{ color: '#1B2A4A' }} />
              <h3 className="text-sm font-bold" style={{ color: '#1B2A4A' }}>
                Gold Masters <span className="font-normal" style={{ color: '#9CA3AF' }}>· never auto-rotated</span>
              </h3>
            </div>
            <p className="text-[10px]" style={{ color: '#9CA3AF' }}>{filteredGold.length} of {goldList.length} shown</p>
          </div>
          <div className="divide-y" style={{ borderColor: '#E5E1DC' }}>
            {filteredGold.map(f => (
              <div key={f.name} className="py-2.5 flex items-start justify-between gap-3 group relative">
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-semibold flex items-center gap-1.5" style={{ color: '#1B2A4A' }}>
                    <Pin size={11} style={{ color: '#1B2A4A' }} />
                    {f.label || f.name}
                    <Info size={11} style={{ color: '#9CA3AF' }} className="cursor-help" />
                  </p>
                  {f.note && <p className="text-[11px] mt-0.5" style={{ color: '#6B7280' }}>{f.note}</p>}
                  <p className="text-[10px] mt-0.5" style={{ color: '#9CA3AF' }}>
                    {fmtDate(f.mtime)} · {fmtBytes(f.size_bytes)}
                    {f.total_documents ? ` · ${f.total_documents.toLocaleString('en-IN')} docs` : ''}
                    {f.collection_total ? ` · ${f.collection_total} collections` : ''}
                    <span className="ml-1 font-mono">{f.name}</span>
                  </p>
                  {/* Hover tooltip — what's inside this snapshot */}
                  <div className="absolute left-0 top-full z-20 mt-1 hidden group-hover:block w-80 rounded-lg shadow-xl border p-3 text-[11px]"
                    style={{ backgroundColor: '#FFF', borderColor: '#1B2A4A' }}
                    data-testid={`snapshot-gold-tooltip-${f.name}`}>
                    <p className="text-[10px] uppercase tracking-wider mb-1" style={{ color: '#1B2A4A' }}>Snapshot contents</p>
                    <p className="mb-2" style={{ color: '#6B7280' }}>
                      Captured {fmtDate(f.captured_at || f.mtime)}<br/>
                      {(f.total_documents || 0).toLocaleString('en-IN')} docs across {f.collection_total || 0} collections · {fmtBytes(f.size_bytes)}
                    </p>
                    {f.has_code && (
                      <p className="mb-2 p-1.5 rounded text-[10px]" style={{ backgroundColor: '#ECFDF5', color: '#2D8B5E' }}>
                        ✅ Full codebase included · {f.code_files} source files ({fmtBytes(f.code_bytes)}) + {f.doc_files} docs
                      </p>
                    )}
                    {Array.isArray(f.top_collections) && f.top_collections.length > 0 && (
                      <>
                        <p className="text-[10px] font-semibold mb-1" style={{ color: '#1B2A4A' }}>Top collections</p>
                        <div className="grid grid-cols-2 gap-x-3 gap-y-0.5">
                          {f.top_collections.map((c) => (
                            <div key={c.name} className="flex items-center justify-between">
                              <span className="truncate" style={{ color: '#6B7280' }}>{c.name}</span>
                              <span className="font-semibold" style={{ color: '#1B2A4A' }}>{(c.count || 0).toLocaleString('en-IN')}</span>
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <button onClick={() => downloadAuto(f.name)}
                    disabled={busy === f.name}
                    className="px-2.5 py-1 rounded-md text-[11px] font-medium border flex items-center gap-1 disabled:opacity-60"
                    style={{ borderColor: '#E5E1DC', color: '#2A7B88' }}
                    data-testid={`snapshot-gold-dl-${f.name}`}>
                    <Download size={11} /> {busy === f.name ? '…' : 'Download'}
                  </button>
                  <button onClick={() => deleteGold(f.name, f.label)}
                    className="px-2 py-1 rounded-md text-[11px] font-medium border flex items-center gap-1"
                    style={{ borderColor: '#FECACA', color: '#D4726A' }}
                    data-testid={`snapshot-gold-del-${f.name}`}>
                    <Trash2 size={11} />
                  </button>
                </div>
              </div>
            ))}
            {filteredGold.length === 0 && (
              <p className="text-center text-[11px] py-4" style={{ color: '#9CA3AF' }}>No gold masters match "{search}"</p>
            )}
          </div>
        </div>
      )}

      {/* Auto-snapshots list */}
      <div className="bg-white rounded-xl border p-5" style={{ borderColor: '#E5E1DC' }} data-testid="snapshot-auto-list">
        <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
          <div className="flex items-center gap-2">
            <Clock size={16} style={{ color: '#2A7B88' }} />
            <h3 className="text-sm font-bold" style={{ color: '#1B2A4A' }}>Scheduled Auto-Backups</h3>
          </div>
          <p className="text-[10px]" style={{ color: '#9CA3AF' }}>
            Runs daily 08:00 IST · retains last 7 · {filteredAuto.length} of {autoList.length} shown
          </p>
        </div>

        {/* Search + sort controls — addresses "not moving and filtering properly" */}
        <div className="flex items-center gap-2 mb-3 flex-wrap" data-testid="snapshot-controls">
          <div className="relative flex-1 min-w-[180px]">
            <Search size={11} className="absolute left-2 top-1/2 -translate-y-1/2" style={{ color: '#9CA3AF' }} />
            <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by filename, label, or note…"
              className="w-full text-[11px] pl-7 pr-2 py-1.5 rounded border"
              style={{ borderColor: '#E5E1DC' }}
              data-testid="snapshot-search" />
          </div>
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}
            className="text-[11px] px-2 py-1.5 rounded border bg-white"
            style={{ borderColor: '#E5E1DC', color: '#6B7280' }}
            data-testid="snapshot-sort">
            <option value="newest">Newest first</option>
            <option value="oldest">Oldest first</option>
            <option value="largest">Largest first</option>
            <option value="smallest">Smallest first</option>
          </select>
        </div>

        {filteredAuto.length === 0 ? (
          <p className="text-xs py-6 text-center" style={{ color: '#9CA3AF' }}>
            {autoList.length === 0
              ? 'No auto-backups written yet. Click "Run Auto-Backup Now" to generate the first one.'
              : `No auto-backups match "${search}".`}
          </p>
        ) : (
          <div className="divide-y" style={{ borderColor: '#E5E1DC' }}>
            {filteredAuto.map(f => (
              <div key={f.name} className="flex items-center justify-between py-2 group relative">
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-semibold flex items-center gap-1.5" style={{ color: '#1B2A4A' }}>
                    {f.name}
                    <Info size={11} style={{ color: '#9CA3AF' }} className="cursor-help" />
                  </p>
                  <p className="text-[10px]" style={{ color: '#9CA3AF' }}>
                    {fmtDate(f.mtime)} · {fmtBytes(f.size_bytes)}
                    {f.total_documents ? ` · ${f.total_documents.toLocaleString('en-IN')} docs` : ''}
                    {f.collection_total ? ` · ${f.collection_total} collections` : ''}
                  </p>
                  {/* Hover tooltip */}
                  <div className="absolute left-0 top-full z-20 mt-1 hidden group-hover:block w-80 rounded-lg shadow-xl border p-3 text-[11px]"
                    style={{ backgroundColor: '#FFF', borderColor: '#2A7B88' }}
                    data-testid={`snapshot-auto-tooltip-${f.name}`}>
                    <p className="text-[10px] uppercase tracking-wider mb-1" style={{ color: '#2A7B88' }}>Snapshot contents</p>
                    <p className="mb-2" style={{ color: '#6B7280' }}>
                      Captured {fmtDate(f.captured_at || f.mtime)}<br/>
                      {(f.total_documents || 0).toLocaleString('en-IN')} docs across {f.collection_total || 0} collections · {fmtBytes(f.size_bytes)}
                    </p>
                    {f.has_code && (
                      <p className="mb-2 p-1.5 rounded text-[10px]" style={{ backgroundColor: '#ECFDF5', color: '#2D8B5E' }}>
                        ✅ Full codebase included · {f.code_files} source files ({fmtBytes(f.code_bytes)}) + {f.doc_files} docs
                      </p>
                    )}
                    {Array.isArray(f.top_collections) && f.top_collections.length > 0 && (
                      <>
                        <p className="text-[10px] font-semibold mb-1" style={{ color: '#2A7B88' }}>Top collections</p>
                        <div className="grid grid-cols-2 gap-x-3 gap-y-0.5">
                          {f.top_collections.map((c) => (
                            <div key={c.name} className="flex items-center justify-between">
                              <span className="truncate" style={{ color: '#6B7280' }}>{c.name}</span>
                              <span className="font-semibold" style={{ color: '#1B2A4A' }}>{(c.count || 0).toLocaleString('en-IN')}</span>
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                </div>
                <button onClick={() => downloadAuto(f.name)}
                  disabled={busy === f.name}
                  className="px-2.5 py-1 rounded-md text-[11px] font-medium border flex items-center gap-1 disabled:opacity-60 shrink-0"
                  style={{ borderColor: '#E5E1DC', color: '#2A7B88' }}
                  data-testid={`snapshot-auto-dl-${f.name}`}>
                  <Download size={11} /> {busy === f.name ? '…' : 'Download'}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
